import { removeAWSID } from "auth/modules/util";
import { LoadingSpinner } from "@xapp/components";
import { StandardLayout } from "layouts";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import { Credentials, State } from "redux/state";


function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export interface LinkAWSIDProps {
    api: string;
}

/**
 * We get redirected here when we link the AWS ID
 *
 * Redirected from https://api.xapp.ai/aws/mkt/link
 * @param props
 */
export const LinkAWSID: FC<LinkAWSIDProps> = (props) => {
    let { api } = props;

    if (!api) {
        api = window.xaStudioConfig?.apiUrl;
    }

    const __SUPPORT_EMAIL__ = window?.xaStudioConfig?.supportEmail;

    const query = useQuery();
    const awsIdQuery = query.get("awsId");
    const awsIdState = useSelector<State, string>(state => state.auth.awsId);
    const credentials = useSelector<State, Credentials>(state => state.auth.credentials);

    const [awsId] = useState<string>(awsIdQuery || awsIdState);
    const [error, setError] = useState<Error>();
    const [success, setSuccess] = useState<boolean>(false);

    useEffect(() => {
        const linkUser = async (accessToken: string, awsMarketplaceId: string) => {
            let status: number;

            await fetch(`${api}/aws/mkt/user`, {
                body: JSON.stringify({
                    awsMarketplaceId
                }),
                headers: {
                    "x-xapp-usertoken": accessToken
                },
                method: "POST"
            })
                .then(response => {
                    status = response.status;

                    return response.json();
                })
                .then(json => {
                    if (status >= 400) {
                        setError(new Error(json.message));
                    } else {
                        // clear out the awsId from localstorage
                        removeAWSID();
                        setSuccess(true);
                    }
                })
                .catch(e => {
                    setError(e);
                });
        };

        if (awsId) {
            linkUser(credentials.accessToken, awsId);
        } else {
            setError(
                new Error(
                    `Query parameter awsId is required to link your marketplace account.  Please return to the listing and try again.`
                )
            );
        }
    }, [api, awsId, credentials.accessToken]);

    if (error) {
        return (
            <StandardLayout>
                <section>
                    <h2>There was an error linking your AWS Marketplace account.</h2>
                    <p>
                        Please contact support if the problem persists at{" "}
                        <a href={`mailto:${__SUPPORT_EMAIL__}`}>{__SUPPORT_EMAIL__}</a>.
                    </p>

                    <pre style={{ overflow: "auto" }}>{error.message}</pre>
                </section>
            </StandardLayout>
        );
    }

    if (success) {
        return <Redirect to={"/onboarding"} />;
    }

    return <LoadingSpinner loadingText={"Linking to AWS Marketplace account..."} />;
};
